export type EyeKind = "dot" | "arch" | "closed" | "cross";
export type MouthKind = "none" | "smile" | "frown" | "line" | "o" | "wavy";
export type BubbleKind = "none" | "speak" | "think";

export type Vec = { x: number; y: number };

export type EyePose = {
  kind: EyeKind;
  x: number;
  y: number;
  openness: number;
  pupilX: number;
  pupilY: number;
};

export type MouthPose = {
  kind: MouthKind;
  width: number;
  curvature: number;
  wave: number;
  /** Extra local +Y (down) from the default mouth line. */
  y: number;
  /** Smile: lift the left endpoint (design units). */
  bias: number;
};

export type PaperPose = {
  x: number;
  y: number;
  rotation: number;
  width: number;
  height: number;
  /** Tilt toward/away from the camera, degrees. 0 = facing us; ~60 = lying on the floor. */
  pitch: number;
  /** Tumble around the vertical, degrees. */
  roll: number;
};

export type Pose = {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  eyeL: EyePose;
  eyeR: EyePose;
  brow: number;
  /** 0 = both brows when `brow` > 0; -1 = character-right/screen-left; 1 = character-left/screen-right. */
  browSide: number;
  mouth: MouthPose;
  tail1: Vec & { rx: number; ry: number };
  tail2: Vec & { rx: number; ry: number };
  papers: PaperPose[];
  paperOrbitT: number;
  bubble: {
    progress: number;
    kind: BubbleKind;
    text: string;
    fill?: string;
    tone?: string;
  };
};

export const HEAD_R = 36;
/** Vertical / horizontal radius for the head and both tail ellipses. */
export const ELLIPSE_ASPECT = 0.85;
export const HEAD_RY = HEAD_R * ELLIPSE_ASPECT;
export const HEAD_X = 120;
export const HEAD_Y = 100;
/** Local +Y from head center to the bottom of the upright head (tail gap). */
export const HEAD_OPEN_Y = HEAD_RY;
/** Clockwise degrees from 12 o'clock to the right lip (flick attach). */
export const HEAD_GAP_RIGHT_DEG = 10;
/** Counterclockwise degrees from 12 o'clock to the left lip (open end). */
export const HEAD_GAP_LEFT_DEG = 20;
export const TAIL1_RX = 10;
export const TAIL1_RY = TAIL1_RX * ELLIPSE_ASPECT;
export const TAIL2_RX = 6;
export const TAIL2_RY = TAIL2_RX * ELLIPSE_ASPECT;

const defaultEye = (x: number, pupilX: number, y = -5): EyePose => ({
  kind: "dot",
  x,
  y,
  openness: 1,
  pupilX,
  pupilY: 0,
});

function uprightTails(): { tail1: Pose["tail1"]; tail2: Pose["tail2"] } {
  const gap = TAIL1_RX * 2;
  const tail1Y = HEAD_Y + HEAD_OPEN_Y + gap + TAIL1_RY;
  const tail2Y = tail1Y + TAIL1_RY + gap + TAIL2_RY;
  return {
    tail1: { x: HEAD_X, y: tail1Y, rx: TAIL1_RX, ry: TAIL1_RY },
    tail2: { x: HEAD_X, y: tail2Y, rx: TAIL2_RX, ry: TAIL2_RY },
  };
}

export function defaultPose(): Pose {
  const tails = uprightTails();
  return {
    x: HEAD_X,
    y: HEAD_Y,
    rotation: 0,
    scale: 1,
    eyeL: defaultEye(-5.8, 2.6),
    eyeR: defaultEye(7.0, -2.8, -5.5),
    brow: 0,
    browSide: 0,
    mouth: { kind: "none", width: 24, curvature: 0, wave: 0, y: 0, bias: 0 },
    tail1: tails.tail1,
    tail2: tails.tail2,
    papers: [],
    paperOrbitT: 0,
    bubble: { progress: 0, kind: "none", text: "", fill: undefined, tone: "default" },
  };
}

export function lerpNumber(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpKind<T>(a: T, b: T, t: number): T {
  return t < 0.5 ? a : b;
}

function lerpEye(a: EyePose, b: EyePose, t: number): EyePose {
  return {
    kind: lerpKind(a.kind, b.kind, t),
    x: lerpNumber(a.x, b.x, t),
    y: lerpNumber(a.y, b.y, t),
    openness: lerpNumber(a.openness, b.openness, t),
    pupilX: lerpNumber(a.pupilX, b.pupilX, t),
    pupilY: lerpNumber(a.pupilY, b.pupilY, t),
  };
}

function padPapers(list: PaperPose[], other: PaperPose[]): PaperPose[] {
  if (list.length >= other.length) return list;
  const extras = other.slice(list.length).map((p) => ({ ...p, width: 0, height: 0 }));
  return [...list, ...extras];
}

export function lerpPose(a: Pose, b: Pose, t: number): Pose {
  const papersA = padPapers(a.papers, b.papers);
  const papersB = padPapers(b.papers, a.papers);
  const progress = lerpNumber(a.bubble.progress, b.bubble.progress, t);
  return {
    x: lerpNumber(a.x, b.x, t),
    y: lerpNumber(a.y, b.y, t),
    rotation: lerpNumber(a.rotation, b.rotation, t),
    scale: lerpNumber(a.scale, b.scale, t),
    eyeL: lerpEye(a.eyeL, b.eyeL, t),
    eyeR: lerpEye(a.eyeR, b.eyeR, t),
    brow: lerpNumber(a.brow, b.brow, t),
    browSide: lerpKind(a.browSide, b.browSide, t),
    mouth: {
      kind: lerpKind(a.mouth.kind, b.mouth.kind, t),
      width: lerpNumber(a.mouth.width, b.mouth.width, t),
      curvature: lerpNumber(a.mouth.curvature, b.mouth.curvature, t),
      wave: lerpNumber(a.mouth.wave, b.mouth.wave, t),
      y: lerpNumber(a.mouth.y, b.mouth.y, t),
      bias: lerpNumber(a.mouth.bias, b.mouth.bias, t),
    },
    tail1: {
      x: lerpNumber(a.tail1.x, b.tail1.x, t),
      y: lerpNumber(a.tail1.y, b.tail1.y, t),
      rx: lerpNumber(a.tail1.rx, b.tail1.rx, t),
      ry: lerpNumber(a.tail1.ry, b.tail1.ry, t),
    },
    tail2: {
      x: lerpNumber(a.tail2.x, b.tail2.x, t),
      y: lerpNumber(a.tail2.y, b.tail2.y, t),
      rx: lerpNumber(a.tail2.rx, b.tail2.rx, t),
      ry: lerpNumber(a.tail2.ry, b.tail2.ry, t),
    },
    papers: papersA.map((p, i) => ({
      x: lerpNumber(p.x, papersB[i].x, t),
      y: lerpNumber(p.y, papersB[i].y, t),
      rotation: lerpNumber(p.rotation, papersB[i].rotation, t),
      width: lerpNumber(p.width, papersB[i].width, t),
      height: lerpNumber(p.height, papersB[i].height, t),
      pitch: lerpNumber(p.pitch, papersB[i].pitch, t),
      roll: lerpNumber(p.roll, papersB[i].roll, t),
    })),
    paperOrbitT: lerpNumber(a.paperOrbitT, b.paperOrbitT, t),
    bubble: {
      progress,
      kind: lerpKind(a.bubble.kind, b.bubble.kind, t),
      text: progress === 0 ? a.bubble.text : b.bubble.text || a.bubble.text,
      fill: progress === 0 ? a.bubble.fill : b.bubble.fill ?? a.bubble.fill,
      tone: progress === 0 ? a.bubble.tone : b.bubble.tone ?? a.bubble.tone,
    },
  };
}
